import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { emojiAPI, subscriptionAPI } from '@/lib/api';
import { ArrowLeft, Plus, Trash2, Edit, Check, X, Loader2, BookOpen, Smile, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const ADMIN_EMAIL = "computerwork2022@gmail.com";

const EmojiAdmin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [dictionary, setDictionary] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [activeTab, setActiveTab] = useState('dictionary');
  
  // Add/Edit emoji
  const [showAddEmoji, setShowAddEmoji] = useState(false);
  const [emojiForm, setEmojiForm] = useState({ emoji: '', meaning: '', category: 'general' });
  const [editingEmoji, setEditingEmoji] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Add/Edit recipe
  const [showAddRecipe, setShowAddRecipe] = useState(false);
  const [recipeForm, setRecipeForm] = useState({ name: '', emojis: '', description: '', category: 'general' });
  const [editingRecipe, setEditingRecipe] = useState(null);

  useEffect(() => {
    checkAdminAndLoad();
  }, []);

  const checkAdminAndLoad = async () => {
    try {
      const status = await subscriptionAPI.getStatus();
      if (!status.is_admin) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      setIsAdmin(true);
      await loadData();
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [dictData, recipeData] = await Promise.all([
        emojiAPI.getDictionary(),
        emojiAPI.getRecipes()
      ]);
      setDictionary(dictData.dictionary || []);
      setRecipes(recipeData.recipes || []);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  // Emoji Dictionary CRUD
  const handleAddEmoji = async () => {
    if (!emojiForm.emoji || !emojiForm.meaning) {
      toast.error('Emoji and meaning are required');
      return;
    }
    setSaving(true);
    try {
      await emojiAPI.updateEmoji(emojiForm.emoji, emojiForm.meaning, emojiForm.category);
      toast.success('Emoji added!');
      setShowAddEmoji(false);
      setEmojiForm({ emoji: '', meaning: '', category: 'general' });
      await loadData();
    } catch (error) {
      toast.error('Failed to add emoji');
    } finally {
      setSaving(false);
    }
  };

  const startEditEmoji = (entry) => {
    setEditingEmoji(entry.emoji);
    setEmojiForm({ emoji: entry.emoji, meaning: entry.meaning, category: entry.category || 'general' });
  };

  const saveEditEmoji = async () => {
    setSaving(true);
    try {
      await emojiAPI.updateEmoji(emojiForm.emoji, emojiForm.meaning, emojiForm.category);
      toast.success('Emoji updated!');
      setEditingEmoji(null);
      await loadData();
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const deleteEmoji = async (emoji) => {
    if (!confirm(`Delete ${emoji}?`)) return;
    try {
      await emojiAPI.deleteEmoji(emoji);
      toast.success('Emoji deleted');
      await loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Recipe CRUD
  const handleAddRecipe = async () => {
    if (!recipeForm.name || !recipeForm.emojis) {
      toast.error('Name and emojis are required');
      return;
    }
    setSaving(true);
    try {
      await emojiAPI.updateRecipe(recipeForm);
      toast.success('Recipe added!');
      setShowAddRecipe(false);
      setRecipeForm({ name: '', emojis: '', description: '', category: 'general' });
      await loadData();
    } catch (error) {
      toast.error('Failed to add recipe');
    } finally {
      setSaving(false);
    }
  };

  const startEditRecipe = (recipe) => {
    setEditingRecipe(recipe.recipe_id);
    setRecipeForm({ ...recipe });
  };

  const saveEditRecipe = async () => {
    setSaving(true);
    try {
      await emojiAPI.updateRecipe({ ...recipeForm, recipe_id: editingRecipe });
      toast.success('Recipe updated!');
      setEditingRecipe(null);
      await loadData();
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const deleteRecipe = async (recipeId) => {
    if (!confirm('Delete this recipe?')) return;
    try {
      await emojiAPI.deleteRecipe(recipeId);
      toast.success('Recipe deleted');
      await loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center">
        <Card className="p-8 bg-red-500/20 border-red-500/30 text-center">
          <h2 className="text-2xl font-bold text-red-400 mb-4">Access Denied</h2>
          <p className="text-white/70 mb-4">Admin privileges required.</p>
          <Button onClick={() => navigate('/dashboard')} variant="outline" className="border-white/20 text-white">
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900">
      <nav className="bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/admin')} className="text-white hover:bg-white/10">
            <ArrowLeft className="h-5 w-5 mr-2" /> Back to Admin
          </Button>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Smile className="h-7 w-7 text-yellow-400" />
            Emoji Dictionary Manager
          </h1>
          <div className="w-24"></div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-white/10 mb-8">
            <TabsTrigger value="dictionary" className="text-white data-[state=active]:bg-purple-600">
              <BookOpen className="h-4 w-4 mr-2" /> Dictionary ({dictionary.length})
            </TabsTrigger>
            <TabsTrigger value="recipes" className="text-white data-[state=active]:bg-purple-600">
              <Sparkles className="h-4 w-4 mr-2" /> Recipes ({recipes.length})
            </TabsTrigger>
          </TabsList>

          {/* Dictionary Tab */}
          <TabsContent value="dictionary">
            <Card className="p-6 bg-white/10 border-white/20">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-white">Emoji Meanings</h2>
                <Button 
                  onClick={() => setShowAddEmoji(!showAddEmoji)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Plus className="h-4 w-4 mr-2" /> Add Emoji
                </Button>
              </div>

              {/* Add Emoji Form */}
              {showAddEmoji && (
                <Card className="p-4 bg-green-500/10 border-green-500/30 mb-6">
                  <h3 className="text-lg font-semibold text-green-300 mb-4">Add New Emoji</h3>
                  <div className="grid md:grid-cols-4 gap-4">
                    <Input
                      placeholder="Emoji (e.g., 🎉)"
                      value={emojiForm.emoji}
                      onChange={(e) => setEmojiForm({...emojiForm, emoji: e.target.value})}
                      className="text-2xl bg-white/10 border-white/20 text-white"
                    />
                    <Input
                      placeholder="Meaning"
                      value={emojiForm.meaning}
                      onChange={(e) => setEmojiForm({...emojiForm, meaning: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                    />
                    <Select value={emojiForm.category} onValueChange={(v) => setEmojiForm({...emojiForm, category: v})}>
                      <SelectTrigger className="bg-white/10 border-white/20 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="people">People</SelectItem>
                        <SelectItem value="nature">Nature</SelectItem>
                        <SelectItem value="places">Places</SelectItem>
                        <SelectItem value="animals">Animals</SelectItem>
                        <SelectItem value="objects">Objects</SelectItem>
                        <SelectItem value="moods">Moods</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button onClick={handleAddEmoji} disabled={saving} className="bg-green-600 hover:bg-green-700">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
                    </Button>
                  </div>
                </Card>
              )}

              {/* Emoji List */}
              <div className="grid gap-2 max-h-[500px] overflow-y-auto">
                {dictionary.map((entry) => (
                  <div key={entry.emoji} className="flex items-center gap-4 p-3 bg-white/5 rounded-lg hover:bg-white/10">
                    {editingEmoji === entry.emoji ? (
                      <>
                        <span className="text-3xl w-12">{entry.emoji}</span>
                        <Input
                          value={emojiForm.meaning}
                          onChange={(e) => setEmojiForm({...emojiForm, meaning: e.target.value})}
                          className="flex-1 bg-white/10 border-white/20 text-white"
                        />
                        <Select value={emojiForm.category} onValueChange={(v) => setEmojiForm({...emojiForm, category: v})}>
                          <SelectTrigger className="w-32 bg-white/10 border-white/20 text-white text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General</SelectItem>
                            <SelectItem value="people">People</SelectItem>
                            <SelectItem value="nature">Nature</SelectItem>
                            <SelectItem value="places">Places</SelectItem>
                            <SelectItem value="animals">Animals</SelectItem>
                            <SelectItem value="objects">Objects</SelectItem>
                            <SelectItem value="moods">Moods</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button size="sm" onClick={saveEditEmoji} disabled={saving} className="bg-green-600">
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button size="sm" onClick={() => setEditingEmoji(null)} variant="ghost" className="text-red-400">
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <span className="text-3xl w-12">{entry.emoji}</span>
                        <span className="flex-1 text-white">{entry.meaning}</span>
                        <span className="text-xs text-purple-300 bg-purple-500/20 px-2 py-1 rounded">{entry.category}</span>
                        <Button size="sm" onClick={() => startEditEmoji(entry)} variant="ghost" className="text-blue-400 hover:bg-blue-500/20">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" onClick={() => deleteEmoji(entry.emoji)} variant="ghost" className="text-red-400 hover:bg-red-500/20">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* Recipes Tab */}
          <TabsContent value="recipes">
            <Card className="p-6 bg-white/10 border-white/20">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-white">Preset Recipes</h2>
                <Button 
                  onClick={() => setShowAddRecipe(!showAddRecipe)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Plus className="h-4 w-4 mr-2" /> Add Recipe
                </Button>
              </div>

              {/* Add Recipe Form */}
              {showAddRecipe && (
                <Card className="p-4 bg-green-500/10 border-green-500/30 mb-6">
                  <h3 className="text-lg font-semibold text-green-300 mb-4">Add New Recipe</h3>
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <Input
                      placeholder="Recipe Name"
                      value={recipeForm.name}
                      onChange={(e) => setRecipeForm({...recipeForm, name: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                    />
                    <Input
                      placeholder="Emojis (e.g., 🌅🏖️👩)"
                      value={recipeForm.emojis}
                      onChange={(e) => setRecipeForm({...recipeForm, emojis: e.target.value})}
                      className="text-2xl bg-white/10 border-white/20 text-white"
                    />
                    <Input
                      placeholder="Description"
                      value={recipeForm.description}
                      onChange={(e) => setRecipeForm({...recipeForm, description: e.target.value})}
                      className="bg-white/10 border-white/20 text-white"
                    />
                    <div className="flex gap-2">
                      <Select value={recipeForm.category} onValueChange={(v) => setRecipeForm({...recipeForm, category: v})}>
                        <SelectTrigger className="flex-1 bg-white/10 border-white/20 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="portrait">Portrait</SelectItem>
                          <SelectItem value="landscape">Landscape</SelectItem>
                          <SelectItem value="fantasy">Fantasy</SelectItem>
                          <SelectItem value="sci-fi">Sci-Fi</SelectItem>
                          <SelectItem value="animals">Animals</SelectItem>
                          <SelectItem value="nature">Nature</SelectItem>
                          <SelectItem value="lifestyle">Lifestyle</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button onClick={handleAddRecipe} disabled={saving} className="bg-green-600 hover:bg-green-700">
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
                      </Button>
                    </div>
                  </div>
                </Card>
              )}

              {/* Recipe List */}
              <div className="grid md:grid-cols-2 gap-4">
                {recipes.map((recipe) => (
                  <Card key={recipe.recipe_id} className="p-4 bg-white/5 border-white/10">
                    {editingRecipe === recipe.recipe_id ? (
                      <div className="space-y-3">
                        <Input
                          value={recipeForm.name}
                          onChange={(e) => setRecipeForm({...recipeForm, name: e.target.value})}
                          className="bg-white/10 border-white/20 text-white"
                          placeholder="Name"
                        />
                        <Input
                          value={recipeForm.emojis}
                          onChange={(e) => setRecipeForm({...recipeForm, emojis: e.target.value})}
                          className="text-2xl bg-white/10 border-white/20 text-white"
                          placeholder="Emojis"
                        />
                        <Input
                          value={recipeForm.description}
                          onChange={(e) => setRecipeForm({...recipeForm, description: e.target.value})}
                          className="bg-white/10 border-white/20 text-white"
                          placeholder="Description"
                        />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={saveEditRecipe} disabled={saving} className="bg-green-600">
                            <Check className="h-4 w-4 mr-1" /> Save
                          </Button>
                          <Button size="sm" onClick={() => setEditingRecipe(null)} variant="ghost" className="text-red-400">
                            <X className="h-4 w-4 mr-1" /> Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="text-3xl mb-2">{recipe.emojis}</div>
                        <h3 className="text-lg font-semibold text-white">{recipe.name}</h3>
                        <p className="text-white/60 text-sm mb-3">{recipe.description}</p>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-purple-300 bg-purple-500/20 px-2 py-1 rounded">{recipe.category}</span>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => startEditRecipe(recipe)} variant="ghost" className="text-blue-400">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button size="sm" onClick={() => deleteRecipe(recipe.recipe_id)} variant="ghost" className="text-red-400">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </Card>
                ))}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EmojiAdmin;
